/**
 * ประลองมือ (the duel sample, docs/research/C0_PVP.md): a best-of-three bout with a rival at the hub. Data only; the rules are
 * `systems/duelMatch.ts`, the opponent's mind is `systems/duelBot.ts`. The rival fights with a copy of the player's own
 * build (a mirror match, so the bout is decided by reads and timing, not by gear), as a second player in the World,
 * sending the same commands a person would.
 */

import { RAGE, SKILLS } from './skills';

export const DUEL_MATCH = {
  /** Rounds won to take the bout (best of three). */
  winRounds: 2,
  /** Safety net: rounds played at most (a drawn round is replayed). */
  maxRounds: 5,
  /** A round ends when a fighter falls to this share of their health. */
  floorHp: 0.1,
  /** Seconds: the first countdown, the countdown between rounds, how long a round's result stays, the round clock. */
  countdownFirst: 3,
  countdownNext: 1,
  /** The KO beat: seconds between the last blow and the round banner (the sim keeps stepping, nobody can be hurt). */
  koBeat: 0.5,
  roundResult: 2.2,
  roundSeconds: 50,
  /** The result card waits this long for a choice (rematch / leave) before the player is let go. */
  resultSeconds: 25,
  /** How near (metres) the player must be to challenge the rival. */
  reach: 9,
  /** The ring: its radius round the middle (metres); past it a fighter is pushed back. */
  radius: 11,
  /** The two marks sit this far either side of the middle. */
  markGap: 3.6,
  /** Where the rival stands at the hub, from the spawn (metres). */
  home: [6, 4] as const,
  /** Blows shove the one struck: metres for a landed hit, a guarded hit, a guard-break; and the shove's length (s). */
  knock: { hit: 0.55, blocked: 0.18, broken: 0.9, seconds: 0.12 },
  /** Hits that took at least this share of the struck one's health count as heavy (a longer shove, a louder cue). */
  heavy: 0.1,
  /** Skill of the rival, from 0 (new to the ring) to this many steps; a win raises it, a loss lowers it. */
  maxLevel: 4,
} as const;

/** How the rival fights: weights 0–1 and the pauses of a person. Level (0–4) sharpens the reactions. */
export interface DuelStyle {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  /** Says what they do, shown on the challenge prompt. */
  readonly hint: string;
  /** Presses forward, rather than waiting for the other to move. */
  readonly aggression: number;
  /** Puts up the block when the other is about to strike. */
  readonly guard: number;
  /** Reaches for the guard-breaking move on a raised block. */
  readonly breaker: number;
  /** Fakes: steps in and out of range, drops and raises the guard, to draw out a mistake. */
  readonly feint: number;
  /** Steps aside (the dodge) rather than standing in a blow. */
  readonly evade: number;
  /** Opens a counter stance on a hunch (bait). */
  readonly counter: number;
  /** Wanted distance (metres) between the two in a lull. */
  readonly spacing: number;
  /** Jumps in from range (jump, air dash, dive strike) rather than walking up. */
  readonly jumpIn: number;
  /**
   * What it does with a full rage bar (duel v3 P2c): 'spend' fires the ultimate at once, 'hold' keeps it for a foe that
   * is opened (stunned, guard broken, in recovery) or nearly beaten, 'bait' shows it as a bluff and fires on an opening.
   */
  readonly rageUse?: 'spend' | 'hold' | 'bait';
}

export const DUEL_STYLES: readonly DuelStyle[] = [
  {
    id: 'fierce',
    name: 'ศิษย์ใจร้อน',
    title: 'บุกไม่หยุด',
    hint: 'ชอบรุกต่อเนื่อง ระวังท่าทำลายตอนท่านตั้งรับ',
    aggression: 0.85,
    guard: 0.25,
    breaker: 0.7,
    feint: 0.15,
    evade: 0.3,
    counter: 0.15,
    spacing: 1.8,
    jumpIn: 0.5,
    rageUse: 'spend',
  },
  {
    id: 'calm',
    name: 'ศิษย์ใจนิ่ง',
    title: 'ตั้งรับรอจังหวะ',
    hint: 'ตั้งรับแล้วสวนกลับ อย่าฟาดรัวๆ ใส่การตั้งรับ',
    aggression: 0.35,
    guard: 0.75,
    breaker: 0.45,
    feint: 0.3,
    evade: 0.35,
    counter: 0.6,
    spacing: 2.6,
    jumpIn: 0.3,
    rageUse: 'hold',
  },
  {
    id: 'sly',
    name: 'ศิษย์เจ้าเล่ห์',
    title: 'หลอกล่อ',
    hint: 'ชอบเดินเข้าออกหลอกให้พลาด แล้วฉวยจังหวะ',
    aggression: 0.55,
    guard: 0.45,
    breaker: 0.55,
    feint: 0.8,
    evade: 0.55,
    counter: 0.45,
    spacing: 3.2,
    jumpIn: 0.9,
    rageUse: 'bait',
  },
];

/**
 * Reaction time (seconds between something showing and the rival acting on it) by level: a person needs about a
 * quarter of a second to read a wind-up; the sharpest rival sees it in 0.22 s.
 */
export const DUEL_REACT: readonly number[] = [0.62, 0.46, 0.34, 0.28, 0.22];

/** Chance the rival answers a read correctly (an interrupt, a dodge, a guard in time) by level. */
export const DUEL_SKILL: readonly number[] = [0.3, 0.48, 0.65, 0.78, 0.88];

/** Names the rival takes in the stands of the sample (original). */
export const DUEL_NAMES: readonly string[] = ['เฉินเยว่', 'ฟางหลิน', 'ลู่เหวย', 'ซ่งเจิ้น', 'เหลียงอัน'];

/**
 * Opt-in switches (round 2, docs/research/C0_PVP.md §9). The main game leaves them off, so its rules are unchanged; the
 * duel clone (apps/c0-duel) and the dev server turn them on before a World is made.
 * - `v2`: rivals fight with their own build, the timed parry, round stats, the shown ring, best-of-three between players;
 * - `rival`: the hub rival stands in the world (a server's zone World has none).
 */
export const DUEL_FLAGS = { v2: false, rival: true };
export const setDuelV2 = (v2: boolean, rival = true): void => {
  DUEL_FLAGS.v2 = v2;
  DUEL_FLAGS.rival = rival;
  tellBigMoves(v2);
  fullBarUltimates(v2);
  (RAGE as { perHitTaken: number }).perHitTaken = v2 ? DUEL_RAGE.taken : RAGE_TAKEN_WORLD;
  Object.assign(DUEL_MATCH as { -readonly [K in keyof typeof MATCH_V1]: number }, v2 ? DUEL_FORMAT.match : MATCH_V1);
};

/**
 * The bout's format (user, 2026-10-11, duel v2 bouts): ONE bout of `match.roundSeconds` (2 minutes), no rounds; a fall (to the
 * health floor) wins, the clock out goes to the higher share of health (equal: a draw). The ring's edge is no wall: a fighter
 * may walk, slide or be knocked out of the ring, and whoever stays out for more than `outLimit` seconds in one go loses
 * (stepping back in resets the count, user 2026-10-11). Systems: duelMatch (the clock, the result), duelRingOut (the
 * out-of-ring count), duelBotRing (the bot keeps in, holds the edge).
 */
export const DUEL_FORMAT = {
  match: { winRounds: 1, maxRounds: 1, roundSeconds: 120 },
  /** Seconds of one continuous stay out of the ring that lose the bout. */
  outLimit: 10,
  /** The ring is no wall (a fighter can be out of it). */
  ringOut: true,
} as const;
/** The rounds of the first duel (best of three), put back when the flag is off. */
const MATCH_V1 = { winRounds: DUEL_MATCH.winRounds, maxRounds: DUEL_MATCH.maxRounds, roundSeconds: DUEL_MATCH.roundSeconds };

/**
 * Rage in a bout (duel v3 P2, the user's choice 2: the 9yin way). The world's `RAGE` is untouched (max 30, +4 for a blow
 * taken, which a guarded blow counts as); in a bout a blow taken gives `taken` (2) in place of that 4, a blow guarded gives the guard `guarded` more, a landed
 * blow gives the one who landed it `landed`, and an ultimate (a power move) costs the FULL bar (`RAGE.max`), so each
 * fighter has about one a round (rage is back to nothing every round).
 */
export const DUEL_RAGE = { guarded: 1, landed: 1, taken: 2 };
/** The world's rage for a blow taken (PvE); a bout swaps it for `DUEL_RAGE.taken` and the flag off puts it back. */
const RAGE_TAKEN_WORLD: number = RAGE.perHitTaken;

const RAGE_WAS = new Map<string, number>();
function fullBarUltimates(on: boolean): void {
  for (const sk of Object.values(SKILLS) as { id: string; rage?: number }[]) {
    if (on) {
      if (sk.rage) {
        RAGE_WAS.set(sk.id, sk.rage);
        sk.rage = RAGE.max;
      }
    } else if (RAGE_WAS.has(sk.id)) {
      sk.rage = RAGE_WAS.get(sk.id)!;
      RAGE_WAS.delete(sk.id);
    }
  }
}

/**
 * Big moves get a tell (round 4): in the duel an attack that is a flurry (2+ hits), a gap-closer, a heavy blow, or an area
 * wheel, and has no wind-up of its own, gets one (a visible cast the other can answer, as a ท่าทำลาย has), the longer the
 * more it can take. The moves are data of the World's (`SKILLS`); this opt-in switch sets the wind-up when the duel is on and
 * puts the old value back when it is off, so the main game's moves are as they were.
 */
const WINDUP_WAS = new Map<string, number>();
export const bigMove = (s: { kind: string; hits?: number; dash?: unknown; power: number; aoe?: number; spin?: unknown }): boolean =>
  s.kind === 'attack' && ((s.hits ?? 1) >= 2 || !!s.dash || s.power >= 1.9 || !!s.aoe || !!s.spin);
function tellBigMoves(on: boolean): void {
  for (const sk of Object.values(SKILLS) as { id: string; windup: number; kind: string; hits?: number; dash?: unknown; power: number; aoe?: number; spin?: unknown }[]) {
    if (on) {
      if (sk.windup === 0 && bigMove(sk)) {
        WINDUP_WAS.set(sk.id, sk.windup);
        sk.windup = (sk.hits ?? 1) >= 3 || sk.power >= 2.2 ? DUEL_V2.bigWindup + 0.1 : DUEL_V2.bigWindup;
      }
    } else if (WINDUP_WAS.has(sk.id)) {
      sk.windup = WINDUP_WAS.get(sk.id)!;
      WINDUP_WAS.delete(sk.id);
    }
  }
}

/** The numbers of round 2 (the guard system, round 3: docs/research/C0_PVP.md §10). */
export const DUEL_V2 = {
  /** Blows shove the one struck, smaller than round 1 (metres): a hit, a guarded hit, a broken guard, a countered attacker. */
  knock: { hit: 0.4, blocked: 0.1, broken: 0.7, parried: 0.6, seconds: 0.12 },
  /** The ring (request 003: 1.5x of 7 m, wider for dashes and lock forms) is small enough to fit the screen and its edge is in play: radius and the marks' distance from the middle (metres). */
  radius: 10.5,
  markGap: 5.1,
  /** Fighters have this many times their health in a bout (rounds of 15-30 s), restored after it. */
  hpMult: 3.75,
  /** No more than this share of a fighter's bar can be lost in one second (a flurry or a big move cannot take a round at once). */
  burst: 0.25,
  /** A move pressed holds the basic cut back this long (a skill is one blow, not a skill and a cut on the same tick). */
  skillGap: 0.6,
  /** The wind-up of a big move that had none (a flurry, a gap-closer, a heavy blow, a wheel); heavier ones 0.1 more. */
  bigWindup: 0.4,
  /** The rival's striking power by stars (0-4): a new fighter hits gently, a master hard (the build's own weight comes on top). */
  rivalAtk: [0.6, 0.75, 0.9, 1, 1.15],
  /** The two do not stand closer than this (metres): a small push apart, so the sprites do not stack. */
  apart: 0.8,
  /** Seconds without any input before a bout pauses. */
  idlePause: 5,
  /** No idle pause in the first seconds of a round (a bout start is not a stop). */
  pauseGrace: 8,
  /** Between two players: how long the result stays before both are sent back. */
  pvpResult: 6,
} as const;

/**
 * แต้มการ์ด (guard points). Each fighter has `max`. A blow that lands on a raised guard only cuts the damage (the guard
 * lets `through` of it pass) and drains guard points by the blow's weight (`perPower` per point of the move's power x 14,
 * so a plain cut takes about 18 and the heavy move about 40); a ท่าทำลาย breaks the guard at once. The guard points come
 * back at `regen` a second a moment (`regenDelay`) after the last blow on the guard, while the guard is down. At zero the
 * guard BREAKS: a stagger (`stagger`), then `brokenSeconds` with no guard at all, and it comes back at `recoverTo`.
 * Blocking no longer costs พลังกาย (that is left to the dodge step): `staminaFloor` keeps a guard from ever running dry.
 */
export const DUEL_GUARD = {
  max: 55,
  perPower: 1.35,
  regen: 14,
  regenDelay: 1,
  stagger: 0.5,
  brokenSeconds: 3,
  recoverTo: 22,
  through: 0.35,
  staminaFloor: 45,
} as const;

/**
 * The triangle with teeth (duel v3 P1, ns-balance's "triFast" set; systems/duelRecovery.ts). Breaks are slow to come back:
 * `breakCd` seconds of cooldown after one that connected, `breakCdWhiff` after one that did not; a break on a foe who is
 * not guarding (nor staggered) does `breakOpenMult` of its damage; a break's wind-up is `windupCut` shorter (never under
 * `windupFloor`).
 */
export const DUEL_TRI = { breakCd: 2.5, breakCdWhiff: 3.5, breakOpenMult: 0.6, windupCut: 0.3, windupFloor: 0.2 };

/**
 * Counter stances (ท่าสวนกลับ): the buff moves that open one. Pressing the move opens a window of `window` seconds
 * (a gold ring at the feet); an attack that lands on that fighter in the window is undone (health and guard points
 * given back) and answered at once with a counter blow of `power`, which staggers for `stun`. A ท่าทำลาย is not
 * countered: it cuts through the stance (read it and break). Every move here is an existing buff of an art, so only the
 * duel (round 2 on) reads it so; the cooldown is the move's own.
 */
export interface DuelCounter {
  readonly window: number;
  readonly power: number;
  readonly stun: number;
}
export const DUEL_COUNTERS: Readonly<Record<string, DuelCounter>> = {
  // กระบี่ไผ่ห่มหมอก (the challenger's own kit, key 6): the mist; the legendary nine swords' oath stays for NPCs and bosses
  'ns-oath': { window: 1.2, power: 1.9, stun: 0.6 },
  // ดาบชาดคลั่งไฟ: the frenzy answers blood with blood
  'bs-frenzy': { window: 1.0, power: 1.7, stun: 0.6 },
  // หมัดระฆังก้อง: the bell, a long window and a heavy answer
  'ar-bell': { window: 1.5, power: 1.5, stun: 0.8 },
  // กระบี่ไผ่ห่มหมอก: the mist, a short window and a quick answer
  'bs-mist': { window: 0.9, power: 1.5, stun: 0.5 },
  // กระบี่แท่งหมึกสน: the ward, a calm window and a clean answer
  'jz-ward': { window: 1.1, power: 1.6, stun: 0.6 },
  // อาวุธลับพันดารา: the smoke, a quick window and a sharp answer
  'hw-smoke': { window: 1.0, power: 1.6, stun: 0.5 },
  // กระบี่วงธารา: the focus, a calm window and an answer that turns the force back
  'ts-focus': { window: 1.1, power: 1.7, stun: 0.6 },
};

/** What a rival of a style fights with: a martial art of its own, a neigong, and levels set against the challenger's. */
export interface DuelBuild {
  readonly art: string;
  readonly neigong: string;
  /** Levels over the challenger's own (moves, neigong); the rival's level (0-4) adds to both. */
  readonly skillDelta: number;
  readonly neigongDelta: number;
  /** The build's weight in a fight: health and striking power as multiples (arts differ in what they give; this evens them). */
  readonly hp?: number;
  readonly atk?: number;
}

export const DUEL_BUILDS: Readonly<Record<string, DuelBuild>> = {
  // ดาบชาดคลั่งไฟ: heavy cuts, a charge, a quick break; the neigong of blood
  fierce: { art: 'heat-sabre', neigong: 'ember-heart', skillDelta: 1, neigongDelta: 1, hp: 1.15, atk: 1.15 },
  // หมัดระฆังก้อง: a slow, hard palm, the break and a powerful finisher; the iron guard
  calm: { art: 'bell-fist', neigong: 'road-iron', skillDelta: 3, neigongDelta: 3, hp: 1.55, atk: 1.35 },
  // กระบี่แท่งหมึกสน: brush strokes, the poem (three strokes), a ward; the cloud heart (weighted up: the art gives least)
  sly: { art: 'ink-slate', neigong: 'cloud-heart', skillDelta: 2, neigongDelta: 2, hp: 1.7, atk: 1.9 },
};

/** Persistent lock-on (systems/duelLock.ts): how far a target can be taken, and how far before the lock lets go (metres). */
export const DUEL_LOCK = {
  reach: 14,
  drop: 18,
  queueSeconds: 6,
  /** A form lasts at least this long (seconds): room for the receive sequence (knock-back, kneel or fall, recover). */
  formMin: 1.1,
  /** The form's length: until the first strike lands, between strikes (0.15 s each, from the move), and a tail. */
  formStrike: 0.19,
  formTail: 0.35,
  /** A counter stance open when a form would connect cancels it (the counter answers the blow). One flag: flip it to let forms go through stances. */
  counterCancelsForm: true,
  /** A guard does not stop a form: the target is held in it still guarding; a guarded blow in a form cuts the damage as usual, drains no guard points (no break), and adds this much ความโกรธ on top of the World's per-hit gain (4). */
  formRage: 4,
} as const;

/**
 * Qinggong in the bout (duel v3 P1.5, systems/duelMove.ts + duelAir.ts). Bout only (`DUEL_FLAGS.v2`); the open world keeps its
 * own steps. Starting numbers, ns-balance tunes. Distances are metres, times seconds.
 */
export const DUEL_MOVE = {
  /** A step's i-frames: its own length (COMBAT.dashDuration) plus this. */
  invulnPad: 0.05,
  /** The punishable stop after a step, by direction relative to the foe. */
  stop: { back: 0.12, side: 0.1, fwd: 0.2 },
  /** A side-step covers this share of the distance and curls round the foe. */
  sideDist: 0.8,
  /** A forward step stops this far short of the foe (no passing through). */
  fwdGap: 1.0,
  /** A second step inside the window costs x`spamMult` พลังกาย. */
  spamWindow: 1.5,
  spamMult: 1.5,
  /** Stop after a landing from a jump (not out of a dive that landed). */
  landStop: 0.18,
  /** Jump -> air dash -> dive strike (ท่าโฉบ). */
  dive: { windup: 0.3, ahead: 1.5, hitR: 1.7, power: 1.1, whiffStop: 0.35, minHeight: 4 },
  /** สลัดหลุด: out of ordinary hit-stun with a back-step. */
  escape: { cost: 50, lock: 6, dist: 2.6, stop: 0.2 },
} as const;
